import { FC } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Favorite,
  School,
} from '@mui/icons-material';

import { useRouter } from '@cvt/routing';
import { getMatchLabel } from '@shared/helpers/match';

interface Props extends Universities.University {
}

export const UniversityCard: FC<Props> = university => {
  const router = useRouter();

  return (
    <Card
      component={RouterLink}
      to={router.universities.view(university.id).path}
      variant="elevation"
      elevation={1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        opacity: university.match.isIgnored ? 0.7 : 1,
        textDecoration: 'none',
      }}
    >

      {/* Banner/Logo Section */}
      <Box sx={{ position: 'relative', pt: university.banner ? 0 : 3 }}>
        {university.banner ? (
          <CardMedia
            component="img"
            height="140"
            image={university.banner}
            alt={`${university.name} banner`}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              height: 140,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <School sx={{ fontSize: 60, color: 'white', opacity: 0.5 }} />
          </Box>
        )}
        
        {/* University Logo */}
        <Avatar
          src={university.logo}
          alt={university.name}
          sx={{
            width: 80,
            height: 80,
            position: 'absolute',
            bottom: -40,
            left: 16,
            border: '4px solid white',
            bgcolor: 'background.paper',
          }}
        >
          <School />
        </Avatar>
        {university.match.isFavourite && (
          <Box position="absolute" top={8} right={8}>
            <IconButton color="error" >
              <Favorite fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Content */}
      <CardContent sx={{ pt: 6 }}>
        <Typography variant="body2" color="secondary">
          {university.sponsored ? 'Official Partner' : undefined}
        </Typography>
        <Typography
          variant="h4"
          mb={0}
        >
          {university.name}
        </Typography>
        <Typography
          variant="body2"
        >
          {getMatchLabel(university.match)}
        </Typography>

        {/* Description */}
        {university.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {university.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export const UniversityCardSkeleton: FC = () => {
  return null;
};
